import { useEffect, useRef, useState } from 'react';
import { initialize, removeListeners } from './publicitat-smart';
import { usePublicitat } from 'hooks/usePublicitat';
import { v4 as uuidv4 } from 'uuid';
import __isUndefined from 'lodash/isUndefined';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import styles from './publicitat.module.scss';
import { useViewport } from 'hooks/corporatiu/useViewport';
import useIsServiceEnabled from 'hooks/useIsServiceEnabled';

const _getFormatProps = (format) => {
    format = !format.startsWith('FORMAT_') ? 'FORMAT_' + format : format;
    const formatEstil = format?.toLowerCase().replace('format_', '');
    return {
        format,
        formatEstil
    };
};

const addSmartScript = () => {
    if (document.getElementById('3cat-smartadserver-script') !== null) return;
    const domScript = document.createElement('script');
    domScript.type = 'text/javascript';
    domScript.src = 'https://tagmanager.smartadserver.com/1029/604783/smart.prebid.js';
    domScript.id = '3cat-smartadserver-script';
    document.body.appendChild(domScript);
};

const setDivStyles = (div, styles) => {
    if (!div) return;
    Object.keys(styles).forEach((style) => (div.style[style] = styles[style]));
};

const Publicitat = (props) => {
    const { isMobile, zoomNavigator } = useViewport();
    const isServiceEnabled = useIsServiceEnabled('publicitat-display-3catinfo');
    const { configuracio, mostra_etiqueta = true, mostrarEspaiNoPubli = false, inlineFormat = true } = props;
    const [configuration, setConfiguration] = useState({});
    const [hidePubli, setHidePubli] = useState(!mostrarEspaiNoPubli);
    const [open, setOpen] = useState(true);
    const [ariaLabel, setAriaLabel] = useState('Publicitat');
    const [completePublicitat, setCompletePublicitat] = useState(null);
    const [formatProps, setFormatProps] = useState(_getFormatProps(props.format));
    const { setupPubliFinished } = usePublicitat();
    const [blocId, setBlocId] = useState(uuidv4());
    const [styleLabel, setStyleLabel] = useState({});
    const publiRef = useRef();
    const { format, formatEstil } = formatProps;
    const adType = 'smart';

    const stylesPublicitat = clsx({
        [styles.blocPubli]: true,
        [styles.hidePubli]: hidePubli,
        [styles[formatEstil]]: true,
        [styles.isMobile]: isMobile,
        [styles.ambZoom200]: zoomNavigator >= 2,
        [styles.ambZoom400]: zoomNavigator >= 4
    });

    const setStylesPubli = (detail) => {
        const { formatId } = detail;
        const blocPubli = document.getElementById(`bloc_publicitat_${blocId}`);
        const blocPubliIframe = blocPubli?.querySelector(`#sas_${formatId}_iframe`);
        const blocPubliImg = blocPubli?.querySelector(`img`);
        const adInfo = blocPubli?.querySelector('[id^=sas_adinfo_]');
        const blocPubliSenseIframe = blocPubli?.querySelector(`#sas_${formatId}`);
        if (adInfo) setAriaLabel(`Publicitat: ${adInfo.textContent}`);

        if (blocPubliIframe) {
            const advDiv = blocPubli.querySelector('[id^=sas_advDiv_]');
            const advHolder = advDiv?.querySelector('[id^=sas_advHolder_]');
            const htmlIframe = blocPubliIframe.contentWindow?.document?.getElementsByTagName('html')[0];
            const bodyIframe = blocPubliIframe.contentWindow?.document?.getElementsByTagName('body')[0];
            const imgIframe = blocPubliIframe.contentWindow?.document?.getElementsByTagName('img')[0];

            setDivStyles(advDiv, { width: '100%', height: '100%', position: 'relative' });
            setDivStyles(advHolder, { width: '100%', height: '100%', position: 'relative' });
            setDivStyles(htmlIframe, { width: '100%', height: '100%', position: 'relative' });
            setDivStyles(bodyIframe, {
                width: '100%',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%,-50%)',
                position: 'absolute'
            });
            setDivStyles(blocPubliIframe, { top: '50%', transform: 'translateY(-50%)', position: 'relative' });

            if (zoomNavigator >= 4) {
                const imgIframeSenseIframe = blocPubliSenseIframe.contentWindow?.document?.getElementsByTagName('img')[0];
                setDivStyles(bodyIframe, {
                    width: '100%',
                    position: 'absolute'
                });
                setDivStyles(imgIframeSenseIframe, { width: '100%', height: '30%', top: '35%', position: 'absolute' });
                setDivStyles(imgIframe, { width: '100%', height: '30%', top: '35%', position: 'absolute' });
                setStyleLabel({
                    display: 'block'
                });
            } else if (zoomNavigator >= 2) {
                setDivStyles(imgIframe, { width: '100%', position: 'absolute' });
                setDivStyles(bodyIframe, {
                    width: '100%',
                    position: 'absolute'
                });

                setStyleLabel({
                    display: 'block'
                });
            } else {
                setStyleLabel({
                    display: 'block'
                });
            }
        } else if (blocPubliImg) {
            if (adInfo) blocPubliImg.alt = adInfo.textContent;
            setStyleLabel({
                display: 'block'
            });
        } else {
            const advVideo = blocPubli?.querySelector('[id^=sas_videoDiv_]');
            if (advVideo) {
                setStyleLabel({
                    display: 'block'
                });
            }
        }

        setCloseButton(blocPubli);
    };

    const setCloseButton = (blocPubli) => {
        const closeButton = blocPubli?.querySelector('[class^=sas-close-button-]');
        if (closeButton) {
            closeButton.setAttribute('tabindex', '0');
            closeButton.addEventListener('click', () => setOpen(false));
            closeButton.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    setOpen(false);
                }
            });
        }
    };

    const handleOnCompletePublicitat = ({ detail }) => {
        const { loaded, uuid } = detail;
        if (!loaded || blocId !== uuid) return;
        setHidePubli(false);
        setCompletePublicitat(detail);
    };

    const setPubliFromConfig = (dataConfig) => {
        if (dataConfig && dataConfig.status === 'OK') {
            const portalId = dataConfig.portal;
            const seccioId = dataConfig.seccio;
            const publicitat = dataConfig.publis?.find((publi) => publi.format == formatProps.format);
            const formatId = publicitat?.large_pc;
            const actiu = publicitat?.actiu === 'SI';
            const formatIds = {
                LARGE_PC: publicitat?.large_pc,
                MEDIUM_PC: publicitat?.medium_pc,
                MEDIUM_TABLET: publicitat?.medium_tablet,
                SMALL_TABLET: publicitat?.small_tablet,
                MICRO_MOBILE: publicitat?.micro_mobile
            };

            setConfiguration({
                portalId,
                seccioId,
                publicitat,
                formatId,
                formatIds,
                inlineFormat,
                format,
                actiu,
                uuid: blocId
            });
        }
    };

    useEffect(() => {
        addSmartScript();
        setPubliFromConfig(configuracio);

        document.addEventListener('onCompletePublicitat', handleOnCompletePublicitat);

        return () => {
            document.removeEventListener('onCompletePublicitat', handleOnCompletePublicitat);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (setupPubliFinished && configuration.actiu) {
            initialize(configuration, adType);
        }
        return () => {
            removeListeners();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setupPubliFinished, configuration]);

    useEffect(() => {
        if (!hidePubli && completePublicitat) {
            setStylesPubli(completePublicitat);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hidePubli, completePublicitat]);

    return (
        <>
            {open && (
                <div ref={publiRef} data-testid='publicitat'>
                    {mostrarEspaiNoPubli && (!setupPubliFinished || !configuration.actiu) && (
                        <div className={stylesPublicitat}>
                            <div className={styles.contentPubli}>
                                <div className={`${styles.capsaPubli} R-capsaPubli`}>
                                    <div className={styles.publiContainer}></div>
                                </div>
                            </div>
                        </div>
                    )}
                    {isServiceEnabled && setupPubliFinished && configuration.actiu && (
                        <div className={stylesPublicitat} id={`bloc_publicitat_${blocId}`}>
                            <div className={styles.contentPubli}>
                                <aside
                                    id={'publicitat_' + format}
                                    className={`${styles.aside} M-publicitat smart_${format}`}
                                    aria-label={ariaLabel}
                                    tabIndex='-1'
                                    style={{ display: 'inherit' }}
                                >
                                    <div className={`${styles.capsaPubli} R-capsaPubli`}>
                                        <div id={'sas_container_' + format} className={styles.publiContainer}>
                                            <div id={'sas_' + configuration.formatId}></div>
                                        </div>
                                        {mostra_etiqueta && (
                                            <div style={styleLabel} className={`${styles.etiqueta} eti`} role='presentation'>
                                                <p>Publicitat</p>
                                            </div>
                                        )}
                                    </div>

                                    <noscript>
                                        <a
                                            href={
                                                'https://www3.smartadserver.com/ac?jump=1&nwid=1029&siteid=' +
                                                configuration.portalId +
                                                '&pgid=' +
                                                configuration.seccioId +
                                                '&fmtid=' +
                                                configuration.formatId +
                                                '&visit=m&tmstp=[timestamp]&out=nonrich'
                                            }
                                            target='_blank'
                                            rel='noreferrer'
                                        >
                                            <img
                                                src={
                                                    'https://www3.smartadserver.com/ac?out=nonrich&nwid=1029&siteid=' +
                                                    configuration.portalId +
                                                    '&pgid=' +
                                                    configuration.seccioId +
                                                    '&fmtid=' +
                                                    configuration.formatId +
                                                    '&visit=m&tmstp=[timestamp]'
                                                }
                                                border='0'
                                                alt='publicitat'
                                                role='presentation'
                                            />
                                        </a>
                                    </noscript>
                                </aside>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default Publicitat;
